import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, 
  Trash2, 
  FileDown, 
  CheckCircle, 
  Circle, 
  Filter, 
  HelpCircle,
  Save,
  RefreshCcw
} from 'lucide-react';

const HarvestPlanning = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [plans, setPlans] = useState([]);
  const [growers, setGrowers] = useState([]);
  const [varietals, setVarietals] = useState([]);
  const [vineyards, setVineyards] = useState([]);
  const [selectedVarietalFilter, setSelectedVarietalFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  
  // Load reference data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [growersRes, varietalsRes, vineyardsRes, plansRes] = await Promise.all([
          axios.get('/api/companies?type=grower'),
          axios.get('/api/varietals'),
          axios.get('/api/vineyards'),
          // This endpoint doesn't exist yet, but we'll implement it
          axios.get(`/api/harvest-planning?year=${year}`)
            .catch(() => ({ data: [] })) // Fallback if endpoint not ready
        ]);

        setGrowers(growersRes.data);
        setVarietals(varietalsRes.data);
        setVineyards(vineyardsRes.data);
        
        // If we have plans from the API, use them
        if (plansRes.data.length > 0) {
          setPlans(plansRes.data);
        } else {
          // Otherwise initialize with some empty rows
          setPlans([createEmptyPlan(), createEmptyPlan()]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [year]);

  // Create an empty plan row
  const createEmptyPlan = () => ({
    id: Date.now() + Math.random(), // Temporary client ID
    year: year,
    status: 'Uncommitted',
    growerID: '',
    grower: '',
    varietalID: '',
    varietal: '',
    vineyardID: '',
    vineyard: '',
    block: '',
    quantity: 0, // tons
    pricePerTon: 0,
    total: 0,
    yield: 3, // Default yield estimate (tons per acre)
    estimatedCases: 0,
    notes: ''
  });

  // Add a new row
  const addPlan = () => {
    setPlans([...plans, createEmptyPlan()]);
  };

  // Remove a row
  const removePlan = (id) => {
    if (window.confirm('Are you sure you want to remove this plan?')) {
      setPlans(plans.filter(plan => plan.id !== id));
    }
  };

  // Handle field changes
  const handleChange = (id, field, value) => {
    setPlans(plans.map(plan => {
      if (plan.id !== id) return plan;
      
      const updatedPlan = { ...plan, [field]: value };
      
      // Handle special cases
      if (field === 'growerID' && value) {
        const selectedGrower = growers.find(g => g.id.toString() === value.toString());
        updatedPlan.grower = selectedGrower ? selectedGrower.companyName : '';
      }
      
      if (field === 'varietalID' && value) {
        const selectedVarietal = varietals.find(v => v === value);
        updatedPlan.varietal = selectedVarietal || '';
      }
      
      if (field === 'vineyardID' && value) {
        const selectedVineyard = vineyards.find(v => v.id.toString() === value.toString());
        updatedPlan.vineyard = selectedVineyard ? selectedVineyard.vineyardName : '';
      }
      
      // Recalculate totals
      if (['quantity', 'pricePerTon'].includes(field)) {
        const quantity = parseFloat(updatedPlan.quantity) || 0;
        const pricePerTon = parseFloat(updatedPlan.pricePerTon) || 0;
        updatedPlan.total = quantity * pricePerTon;
      }
      
      // Recalculate estimated cases
      if (['quantity', 'yield'].includes(field)) {
        const quantity = parseFloat(updatedPlan.quantity) || 0;
        const yieldValue = parseFloat(updatedPlan.yield) || 0;
        // Formula: (yield * tons) / 2.377
        updatedPlan.estimatedCases = Math.round((yieldValue * quantity) / 2.377);
      }
      
      return updatedPlan;
    }));
  };

  // Filter plans based on selections
  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      if (selectedVarietalFilter && plan.varietal !== selectedVarietalFilter) {
        return false;
      }
      if (selectedStatusFilter && plan.status !== selectedStatusFilter) {
        return false;
      }
      return true;
    });
  }, [plans, selectedVarietalFilter, selectedStatusFilter]);

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    return filteredPlans.reduce(
      (totals, plan) => {
        totals.totalTons += parseFloat(plan.quantity) || 0;
        totals.totalCost += parseFloat(plan.total) || 0;
        totals.totalCases += parseFloat(plan.estimatedCases) || 0;
        
        // Count by status
        if (plan.status === 'Committed') {
          totals.committedTons += parseFloat(plan.quantity) || 0;
          totals.committedCost += parseFloat(plan.total) || 0;
        } else {
          totals.uncommittedTons += parseFloat(plan.quantity) || 0;
          totals.uncommittedCost += parseFloat(plan.total) || 0;
        }
        
        // Count by varietal
        const varietal = plan.varietal || 'Unknown';
        if (!totals.byVarietal[varietal]) {
          totals.byVarietal[varietal] = {
            tons: 0,
            cost: 0,
            cases: 0
          };
        }
        totals.byVarietal[varietal].tons += parseFloat(plan.quantity) || 0;
        totals.byVarietal[varietal].cost += parseFloat(plan.total) || 0;
        totals.byVarietal[varietal].cases += parseFloat(plan.estimatedCases) || 0;
        
        return totals;
      },
      { 
        totalTons: 0, 
        totalCost: 0, 
        totalCases: 0,
        committedTons: 0,
        committedCost: 0,
        uncommittedTons: 0,
        uncommittedCost: 0,
        byVarietal: {} 
      }
    );
  }, [filteredPlans]);

  // Save plans to the API
  const savePlans = async () => {
    try {
      // Endpoint doesn't exist yet, but when it does:
      await axios.post('/api/harvest-planning', { 
        year, 
        plans: plans.map(plan => ({
          ...plan,
          quantity: parseFloat(plan.quantity) || 0,
          pricePerTon: parseFloat(plan.pricePerTon) || 0,
          yield: parseFloat(plan.yield) || 0
        }))
      });
      alert('Harvest plans saved successfully!');
    } catch (err) {
      console.error('Error saving plans:', err);
      alert('Failed to save harvest plans. Please try again.');
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Status', 'Grower', 'Varietal', 'Vineyard', 'Block', 
      'Quantity (tons)', '$/Ton', 'Total', 'Yield', 'Est. Cases', 'Notes'
    ];
    
    const csvContent = [
      headers.join(','),
      ...filteredPlans.map(plan => [
        plan.status,
        `"${plan.grower}"`,
        `"${plan.varietal}"`,
        `"${plan.vineyard}"`,
        `"${plan.block}"`,
        plan.quantity,
        plan.pricePerTon,
        plan.total,
        plan.yield,
        plan.estimatedCases,
        `"${plan.notes}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `harvest-plan-${year}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Header Toolbar */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h4 className="mb-0">Harvest Planning</h4>
            </div>
            <div className="col-md-8">
              <div className="d-flex justify-content-end gap-2">
                <div className="d-flex align-items-center me-3">
                  <label htmlFor="yearSelect" className="form-label me-2 mb-0">Year:</label>
                  <select
                    id="yearSelect"
                    className="form-select form-select-sm"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value))}
                  >
                    {[...Array(5)].map((_, i) => (
                      <option key={i} value={currentYear - 2 + i}>
                        {currentYear - 2 + i}
                      </option>
                    ))}
                  </select>
                </div>
                
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={savePlans}
                >
                  <Save size={16} className="me-1" />
                  Save
                </button>
                
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={exportToCSV}
                >
                  <FileDown size={16} className="me-1" />
                  Export
                </button>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowHelp(!showHelp)}
                >
                  <HelpCircle size={16} className="me-1" />
                  Help
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Help Section */}
      {showHelp && (
        <div className="card mb-4">
          <div className="card-body">
            <h5>Using the Harvest Planning Tool</h5>
            <p>This tool helps you plan and budget for your upcoming harvest.</p>
            <ul>
              <li><strong>Status:</strong> Mark as "Committed" once you've secured the grapes</li>
              <li><strong>Yield:</strong> Estimated yield in tons per acre (affects case estimates)</li>
              <li><strong>Est. Cases:</strong> Calculated as (yield × tons) ÷ 2.377</li>
              <li><strong>Filters:</strong> Use the filters to focus on specific varietals or status</li>
              <li><strong>Summary:</strong> View totals by varietal and commitment status at the bottom</li>
            </ul>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowHelp(false)}
            >
              Close Help
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-1">
              <Filter size={18} className="text-muted" />
              <span className="ms-2 fw-bold">Filters:</span>
            </div>
            <div className="col-md-3">
              <label className="form-label">Varietal</label>
              <select
                className="form-select"
                value={selectedVarietalFilter}
                onChange={(e) => setSelectedVarietalFilter(e.target.value)}
              >
                <option value="">All Varietals</option>
                {[...new Set(plans.map(p => p.varietal).filter(Boolean))].map(varietal => (
                  <option key={varietal} value={varietal}>{varietal}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Committed">Committed</option>
                <option value="Uncommitted">Uncommitted</option>
              </select>
            </div>
            <div className="col-md-5">
              <div className="alert alert-info py-2 mb-0">
                <div className="row">
                  <div className="col-md-4 fw-bold">
                    Total: {summaryTotals.totalTons.toFixed(2)} tons
                  </div>
                  <div className="col-md-4 fw-bold">
                    Budget: ${summaryTotals.totalCost.toLocaleString()}
                  </div>
                  <div className="col-md-4 fw-bold">
                    Est. Cases: {summaryTotals.totalCases.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main table */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Status</th>
                  <th>Grower</th>
                  <th>Varietal</th>
                  <th>Vineyard</th>
                  <th>Block</th>
                  <th>Tons</th>
                  <th>$/Ton</th>
                  <th>Total</th>
                  <th>Yield</th>
                  <th>Est. Cases</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlans.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={plan.status}
                        onChange={(e) => handleChange(plan.id, 'status', e.target.value)}
                      >
                        <option value="Uncommitted">Uncommitted</option>
                        <option value="Committed">Committed</option>
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={plan.growerID}
                        onChange={(e) => handleChange(plan.id, 'growerID', e.target.value)}
                      >
                        <option value="">Select Grower</option>
                        {growers.map((grower) => (
                          <option key={grower.id} value={grower.id}>
                            {grower.companyName}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={plan.varietal}
                        onChange={(e) => handleChange(plan.id, 'varietal', e.target.value)}
                      >
                        <option value="">Select Varietal</option>
                        {varietals.map((varietal, index) => (
                          <option key={index} value={varietal}>
                            {varietal}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={plan.vineyardID}
                        onChange={(e) => handleChange(plan.id, 'vineyardID', e.target.value)}
                      >
                        <option value="">Select Vineyard</option>
                        {vineyards
                          .filter(v => !plan.growerID || v.companyID.toString() === plan.growerID.toString())
                          .map((vineyard) => (
                            <option key={vineyard.id} value={vineyard.id}>
                              {vineyard.vineyardName}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Block"
                        value={plan.block}
                        onChange={(e) => handleChange(plan.id, 'block', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={plan.quantity}
                        onChange={(e) => handleChange(plan.id, 'quantity', e.target.value)}
                      />
                    </td>
                    <td>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          min="0"
                          //step="0.01"
                          className="form-control form-control-sm"
                          value={plan.pricePerTon}
                          onChange={(e) => handleChange(plan.id, 'pricePerTon', e.target.value)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={plan.total}
                          readOnly
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="form-control form-control-sm"
                        value={plan.yield}
                        onChange={(e) => handleChange(plan.id, 'yield', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={plan.estimatedCases}
                        readOnly
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Notes"
                        value={plan.notes}
                        onChange={(e) => handleChange(plan.id, 'notes', e.target.value)}
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removePlan(plan.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan="12">
                    <button
                      className="btn btn-sm btn-outline-primary w-100"
                      onClick={addPlan}
                    >
                      <Plus size={16} className="me-1" />
                      Add Row
                    </button>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        {/* Status Summary */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Status Summary</h5>
            </div>
            <div className="card-body">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Tons</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="d-flex align-items-center">
                        <CheckCircle size={16} className="text-success me-2" />
                        Committed
                      </span>
                    </td>
                    <td>{summaryTotals.committedTons.toFixed(2)}</td>
                    <td>${summaryTotals.committedCost.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="d-flex align-items-center">
                        <Circle size={16} className="text-warning me-2" />
                        Uncommitted
                      </span>
                    </td>
                    <td>{summaryTotals.uncommittedTons.toFixed(2)}</td>
                    <td>${summaryTotals.uncommittedCost.toLocaleString()}</td>
                  </tr>
                  <tr className="table-secondary">
                    <td><strong>Total</strong></td>
                    <td><strong>{summaryTotals.totalTons.toFixed(2)}</strong></td>
                    <td><strong>${summaryTotals.totalCost.toLocaleString()}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Varietal Summary */}
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Varietal Summary</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Varietal</th>
                      <th>Tons</th>
                      <th>%</th>
                      <th>Cost</th>
                      <th>Avg. $/Ton</th>
                      <th>Est. Cases</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(summaryTotals.byVarietal)
                      .sort(([,a], [,b]) => b.tons - a.tons)
                      .map(([varietal, data]) => (
                        <tr key={varietal}>
                          <td>{varietal}</td>
                          <td>{data.tons.toFixed(2)}</td>
                          <td>
                            {summaryTotals.totalTons > 0
                              ? ((data.tons / summaryTotals.totalTons) * 100).toFixed(1)
                              : '0.0'}%
                          </td>
                          <td>${data.cost.toLocaleString()}</td>
                          <td>
                            ${data.tons > 0
                              ? (data.cost / data.tons).toFixed(0)
                              : '0'}
                          </td>
                          <td>{data.cases.toLocaleString()}</td>
                        </tr>
                      ))}
                    <tr className="table-secondary">
                      <td><strong>Total</strong></td>
                      <td><strong>{summaryTotals.totalTons.toFixed(2)}</strong></td>
                      <td><strong>100%</strong></td>
                      <td><strong>${summaryTotals.totalCost.toLocaleString()}</strong></td>
                      <td>
                        <strong>
                          ${summaryTotals.totalTons > 0
                            ? (summaryTotals.totalCost / summaryTotals.totalTons).toFixed(0)
                            : '0'}
                        </strong>
                      </td>
                      <td><strong>{summaryTotals.totalCases.toLocaleString()}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .table-responsive {
          overflow-x: auto;
        }
        .btn svg {
          vertical-align: middle;
          margin-top: -2px;
        }
        .btn:hover svg {
          transform: scale(1.1);
          transition: transform 0.2s ease;
        }
        .btn:active svg {
          transform: scale(0.95);
        }
          /* Add this CSS to remove number input spinners */
        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { 
          -webkit-appearance: none; 
          margin: 0; 
        }
        
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
    </div>
  );
};

export default HarvestPlanning;